// =============================================================================
// MeuDriver — Admin: edição de platform_settings
// =============================================================================

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Settings, Save, Loader2, AlertTriangle, CalendarX } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'

const KEY_LABELS: Record<string, string> = {
  platform_fee_percent: 'Taxa da plataforma (% acrescido ao preço do instrutor)',
  payout_window_hours: 'Prazo para liberar o repasse após a aula (horas)',
  premium_discount_percent: 'Desconto do aluno Premium em todas as aulas (%)',
  trial_price_multiplier: 'Multiplicador de preço da aula experimental',
  referral_credit_referrer: 'Crédito de indicação — para quem indicou (R$)',
  referral_credit_referred: 'Crédito de indicação — para quem foi indicado (R$)',
  inactivity_days_for_coupon: 'Dias sem aula para enviar cupom de reativação',
  reactivation_coupon_percent: 'Desconto do cupom de reativação (%)',
  waitlist_non_premium_delay_minutes: 'Atraso do aviso da lista de espera para não Premium (minutos)',
  trip_window_before_minutes: 'Rastreamento: minutos antes da aula em que o instrutor pode iniciar',
  trip_window_after_minutes: 'Rastreamento: minutos depois do horário em que ainda pode rastrear',
  trip_ping_retention_days: 'Rastreamento: dias guardando as posições antes de apagar',
  whatsapp_notifications_enabled: 'Avisos por WhatsApp (n8n + Evolution)',
  maintenance_mode: 'Modo manutenção ativo',
  maintenance_message: 'Mensagem de manutenção',
}

// Avisos para chaves cujo valor também aparece fixo em textos do site.
const KEY_WARNINGS: Record<string, string> = {
  whatsapp_notifications_enabled: 'Ligue só depois de importar e ativar o fluxo "[MeuDriver] Avisos por WhatsApp" no n8n — senão os avisos ficam parados na fila.',
  platform_fee_percent: 'Muda a cobrança na hora, mas os textos e simulações do site mostram 40% fixo — peça o ajuste do código junto.',
  premium_discount_percent: 'Muda o desconto cobrado, mas a página do Premium anuncia 10% fixo — peça o ajuste do código junto.',
}

// Chaves tratadas com UI dedicada — excluir do grid genérico
const MAINTENANCE_KEYS = ['maintenance_mode', 'maintenance_message'] as const

// Chaves que ninguém lê: as regras de cancelamento valem pela tabela
// `cancellation_policies` (trigger bookings_settle_credit) e ausência do aluno
// consome 100% do crédito. Mostrá-las fazia o admin achar que mudava a regra.
const DEAD_KEYS = ['cancellation_full_refund_hours', 'cancellation_half_refund_hours', 'no_show_charge_percent']

interface CancellationPolicy {
  id: string
  name: string
  hours_before: number
  refund_percentage: number
  description: string | null
}

function CancellationRulesCard() {
  const { data: policies } = useQuery({
    queryKey: ['cancellation_policies'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cancellation_policies')
        .select('id, name, hours_before, refund_percentage, description')
        .order('hours_before', { ascending: false })
      if (error) throw error
      return (data ?? []) as CancellationPolicy[]
    },
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base text-gray-800">
          <CalendarX className="h-4 w-4 text-gray-400" />
          Regras de cancelamento e ausência
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {(policies ?? []).map((p) => (
          <div key={p.id} className="flex flex-col gap-0.5 rounded-lg border bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-medium text-gray-800">{p.name}</span>
            <span className="text-gray-600">
              {p.hours_before > 0 ? `${p.hours_before} h ou mais antes` : 'menos que isso'} → devolve {p.refund_percentage}%
            </span>
          </div>
        ))}
        <div className="flex flex-col gap-0.5 rounded-lg border bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-medium text-gray-800">Aluno não compareceu</span>
          <span className="text-gray-600">aula conta como dada → devolve 0%</span>
        </div>
        <div className="flex flex-col gap-0.5 rounded-lg border bg-gray-50 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-medium text-gray-800">Instrutor cancelou</span>
          <span className="text-gray-600">a qualquer momento → devolve 100%</span>
        </div>
        <p className="pt-1 text-xs text-gray-400">
          Para mudar essas regras é preciso alterar o sistema (tabela de políticas de cancelamento) — fale com o desenvolvimento.
        </p>
      </CardContent>
    </Card>
  )
}

export default function AdminPlatformSettings() {
  const { data: settings, isLoading } = usePlatformSettings()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Record<string, string>>({})

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase.rpc('update_platform_setting', {
        p_key: key,
        p_value: value,
      })
      if (error) throw error
    },
    onSuccess: (_, { key }) => {
      toast.success('Configuração salva.')
      setEditing((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      queryClient.invalidateQueries({ queryKey: ['platform_settings'] })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar')
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    )
  }

  const maintenanceMode = settings?.find((s) => s.key === 'maintenance_mode')
  const maintenanceMessage = settings?.find((s) => s.key === 'maintenance_message')
  const isModeActive = maintenanceMode?.value === 'true'
  const otherSettings = (settings ?? []).filter(
    (s) => !(MAINTENANCE_KEYS as ReadonlyArray<string>).includes(s.key) && !DEAD_KEYS.includes(s.key),
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-gray-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações da plataforma</h1>
          <p className="text-sm text-gray-500">Edite os parâmetros globais do MeuDriver.</p>
        </div>
      </div>

      {/* ─── Seção: Modo Manutenção ─────────────────────────────────────────── */}
      <Card className={isModeActive ? 'border-red-300 bg-red-50/50' : ''}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-gray-800">
            <AlertTriangle className={`h-4 w-4 ${isModeActive ? 'text-red-500' : 'text-gray-400'}`} />
            Modo manutenção
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toggle modo */}
          {maintenanceMode && (
            <div className="flex items-center gap-3">
              <Label htmlFor="maint-toggle" className="text-sm text-gray-700 flex-1">
                {isModeActive ? 'Manutenção ATIVA — banner visível para usuários' : 'Manutenção inativa'}
              </Label>
              <button
                id="maint-toggle"
                role="switch"
                aria-checked={isModeActive}
                onClick={() => {
                  const next = isModeActive ? 'false' : 'true'
                  updateMutation.mutate({ key: 'maintenance_mode', value: next })
                }}
                disabled={updateMutation.isPending}
                data-testid="maintenance-mode-toggle"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  isModeActive ? 'bg-red-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isModeActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Mensagem */}
          {maintenanceMessage && (
            <div className="space-y-1.5">
              <Label htmlFor="maint-message" className="text-sm text-gray-700">
                Mensagem exibida no banner
              </Label>
              <div className="flex gap-2">
                <Textarea
                  id="maint-message"
                  rows={2}
                  value={editing['maintenance_message'] ?? maintenanceMessage.value}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, maintenance_message: e.target.value }))
                  }
                  data-testid="maintenance-message-input"
                />
                <Button
                  size="sm"
                  disabled={
                    editing['maintenance_message'] === undefined ||
                    editing['maintenance_message'] === maintenanceMessage.value ||
                    updateMutation.isPending
                  }
                  onClick={() =>
                    updateMutation.mutate({
                      key: 'maintenance_message',
                      value: editing['maintenance_message']!,
                    })
                  }
                  data-testid="maintenance-message-save"
                >
                  {updateMutation.isPending && updateMutation.variables?.key === 'maintenance_message' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CancellationRulesCard />

      {/* ─── Grid: demais configurações ─────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2">
        {otherSettings.map((s) => {
          const currentEdit = editing[s.key]
          const isDirty = currentEdit !== undefined && currentEdit !== s.value
          return (
            <Card key={s.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-700">
                  {KEY_LABELS[s.key] ?? s.key}
                </CardTitle>
              </CardHeader>
              {s.data_type === 'boolean' ? (
              <CardContent className="flex items-center justify-between gap-2">
                <span className="text-sm text-gray-600">{s.value === 'true' ? 'Ligado' : 'Desligado'}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={s.value === 'true'}
                  aria-label={KEY_LABELS[s.key] ?? s.key}
                  disabled={updateMutation.isPending}
                  onClick={() => updateMutation.mutate({ key: s.key, value: s.value === 'true' ? 'false' : 'true' })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${s.value === 'true' ? 'bg-teal-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${s.value === 'true' ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </CardContent>
              ) : (
              <CardContent className="flex items-center gap-2">
                <Input
                  type={s.data_type === 'text' ? 'text' : 'number'}
                  step={s.data_type === 'numeric' ? '0.01' : '1'}
                  value={currentEdit ?? s.value}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, [s.key]: e.target.value }))
                  }
                  className="font-mono"
                />
                <Button
                  size="sm"
                  disabled={!isDirty || updateMutation.isPending}
                  onClick={() =>
                    updateMutation.mutate({ key: s.key, value: currentEdit! })
                  }
                >
                  {updateMutation.isPending && updateMutation.variables?.key === s.key ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
              </CardContent>
              )}
              {KEY_WARNINGS[s.key] && (
                <p className="px-6 pb-2 text-xs text-amber-700">{KEY_WARNINGS[s.key]}</p>
              )}
              {s.description && (
                <p className="px-6 pb-4 text-xs text-gray-400">{s.description}</p>
              )}
            </Card>
          )
        })}
      </div>

      <p className="text-xs text-gray-400">
        Última atualização:{' '}
        {settings && settings.length > 0
          ? new Date(
              Math.max(...settings.map((s) => new Date(s.updated_at).getTime()))
            ).toLocaleString('pt-BR')
          : '—'}
      </p>
    </div>
  )
}
